/**
 * ============================================================
 * VALIDACIONES CLÍNICAS NOM – SALVIR
 * ============================================================
 *
 * Este módulo contiene validaciones relacionadas con
 * variables clínicas del expediente médico.
 *
 * Estas validaciones se basan principalmente en:
 *
 * NOM-004
 * NOM-024
 **/
 

// ============================================================
// VALIDACIONES CLÍNICAS - SIGNOS VITALES
// ============================================================

// =============================
// PESO (kg)
// =============================

// valida formato ###.###
export const validarFormatoPeso = (value) => {
  const regex = /^\d{0,3}(\.\d{0,3})?$/;
  return regex.test(value);
};

// rango NOM 1–400 kg, o 999 si se desconoce
export const validarRangoPeso = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return false;

  if (num === 999) return true;

  return num >= 1 && num <= 400;
};


// normaliza: quita ceros sobrantes a la izquierda y a la derecha
export const normalizarPesoOnBlur = (value) => {
  if (typeof value !== "string") return "";

  let limpio = value.trim();

  // quitar punto final si quedó incompleto
  if (limpio.endsWith(".")) {
    limpio = limpio.slice(0, -1);
  }

  if (limpio === "" || limpio === ".") return "";

  const num = parseFloat(limpio);
  if (isNaN(num)) return "";

  return String(num);
};

// =============================
// TALLA / ALTURA (m)
// =============================

// Captura flexible para onChange:
// - solo números y punto
// - máximo un punto decimal
// - máximo 3 dígitos numéricos en total
export const validarFormatoAltura = (value) => {
  if (value === "") return true;

  // solo números y punto
  if (!/^[\d.]+$/.test(value)) return false;

  // máximo un punto
  const puntos = (value.match(/\./g) || []).length;
  if (puntos > 1) return false;

  // máximo 3 dígitos numéricos
  const digitos = value.replace(/\D/g, "");
  if (digitos.length > 3) return false;

  return true;
};

// rango final permitido en metros
// rango 0.30–2.20 m, o 999 si se desconoce
export const validarRangoAltura = (value) => {
  if (value === "") return false;
  const num = parseFloat(value);
  if (isNaN(num)) return false;

  if (num === 999) return true;

  return num >= 0.30 && num <= 2.20;
};

// normalización ligera para onBlur
// - trim
// - elimina punto final si quedó incompleto
export const normalizarAlturaOnBlur = (value) => {
  if (typeof value !== "string") return "";

  let limpio = value.trim();

  if (limpio.endsWith(".")) {
    limpio = limpio.slice(0, -1);
  }

  if (limpio === "" || limpio === ".") return "";

  const num = parseFloat(limpio);
  if (isNaN(num)) return "";

  return String(num);
};

// ------------------------------------------------------------
// FORMATO SISTÓLICA
// Permite solo números hasta 3 dígitos
// Se usa en onChange
// Rango permitido: 50 – 300 mmHg
// Se usa en onBlur
// ------------------------------------------------------------
export const validarFormatoSistolica = (valor) => {

  if (valor === "") return true;

  const regex = /^\d{0,3}$/;

  return regex.test(valor);
};

export const validarRangoSistolica = (valor) => {
  if (valor === "") return true;
  const num = parseInt(valor, 10);
  if (isNaN(num)) return false;

  if (num === 0) return true;

  return num >= 50 && num <= 300;
};

export const normalizarEnterosistolicaOnBlur = (value) => {
  if (value === "") return value;

  const num = parseInt(value, 10);
  if (isNaN(num)) return "";

  return String(num);
};

export const validarDiastolicaNoMayorASistolica = (sistolica, diastolica) => {
  const s = parseInt(sistolica, 10);
  const d = parseInt(diastolica, 10);

  if (isNaN(s) || isNaN(d)) return true;

  return d <= s;
};


// ------------------------------------------------------------
// FORMATO DIASTÓLICA
// Permite solo números hasta 3 dígitos
// Se usa en onChange
// Rango permitido: 20 – 200 mmHg
// Se usa en onBlur
// ------------------------------------------------------------
export const validarFormatoDiastolica = (valor) => {

  if (valor === "") return true;

  const regex = /^\d{0,3}$/;

  return regex.test(valor);
};

export const validarRangoDiastolica = (valor) => {
  if (valor === "") return true;
  const num = parseInt(valor, 10);
  if (isNaN(num)) return false;

  if (num === 0) return true;

  return num >= 20 && num <= 200;
};

export const normalizarEnterodiastolicaOnBlur = (value) => {
  if (value === "") return value;

  const num = parseInt(value, 10);
  if (isNaN(num)) return "";

  return String(num);
};

export const validarSistolicaNoMenorADiastolica = (sistolica, diastolica) => {
  const s = parseInt(sistolica, 10);
  const d = parseInt(diastolica, 10);

  if (isNaN(s) || isNaN(d)) return true;

  return s >= d;
};

// =============================
// FRECUENCIA CARDIACA (lpm)
// =============================

// formato: hasta 3 dígitos
export const validarFormatoFrecuenciaCardiaca = (value) => {
  const regex = /^\d{0,3}$/;
  return regex.test(value);
};

// rango NOM: 40 – 220 (0 permitido si se desconoce)
export const validarRangoFrecuenciaCardiaca = (value) => {
  const num = parseInt(value, 10);
  if (isNaN(num)) return false;

  if (num === 0) return true;

  return num >= 40 && num <= 220;
};

export const normalizarEnterofrecuenciacardiacaOnBlur = (value) => {
  if (value === "") return value;

  const num = parseInt(value, 10);
  if (isNaN(num)) return "";

  return String(num);
};


// =============================
// FRECUENCIA RESPIRATORIA (rpm)
// =============================

// formato: hasta 2 dígitos
export const validarFormatoFrecuenciaRespiratoria = (value) => {
  const regex = /^\d{0,2}$/;
  return regex.test(value);
};

// rango NOM: 10 – 99 (0 permitido)
export const validarRangoFrecuenciaRespiratoria = (value) => {
  const num = parseInt(value, 10);
  if (isNaN(num)) return false;

  if (num === 0) return true;

  return num >= 10 && num <= 99;
};

export const normalizarEnterofrecuenciarespiratoriaOnBlur = (value) => {
  if (value === "") return value;

  const num = parseInt(value, 10);
  if (isNaN(num)) return "";

  return String(num);
};

// =============================
// TEMPERATURA (°C)
// =============================

// formato ##.# (máx 2 enteros + 1 decimal)
export const validarFormatoTemperatura = (value) => {
  const regex = /^\d{0,2}(\.\d?)?$/;
  return regex.test(value);
};

// rango NOM: 30 – 44 (0 permitido)
export const validarRangoTemperatura = (value) => {
  const num = parseFloat(value);
  if (isNaN(num)) return false;

  if (num === 0) return true;

  return num >= 30 && num <= 44;
};

// normaliza punto final (ej: "36.")
export const normalizarTemperaturaOnBlur = (value) => {
  if (typeof value === "string" && value.endsWith(".")) {
    return value.slice(0, -1);
  }
  return value;
};


// =============================
// SATURACIÓN DE OXÍGENO (%)
// =============================

// formato hasta 3 dígitos
export const validarFormatoSaturacionOxigeno = (value) => {
  const regex = /^\d{0,3}$/;
  return regex.test(value);
};

// rango NOM: 1 – 100 (0 permitido)
export const validarRangoSaturacionOxigeno = (value) => {
  const num = parseInt(value, 10);
  if (isNaN(num)) return false;

  if (num === 0) return true;

  return num >= 1 && num <= 100;
};

export const normalizarEnterosaturacionOnBlur = (value) => {
  if (value === "") return value;

  const num = parseInt(value, 10);
  if (isNaN(num)) return "";

  return String(num);
};

// =============================
// CIRCUNFERENCIA DE CINTURA (cm)
// =============================

// formato: hasta 3 dígitos
export const validarFormatoCircunferenciaCintura = (value) => {
  const regex = /^\d{0,3}$/;
  return regex.test(value);
};

// rango NOM: 20 – 300 (0 permitido si se desconoce)
export const validarRangoCircunferenciaCintura = (value) => {
  const num = parseInt(value, 10);
  if (isNaN(num)) return false;

  if (num === 0) return true;

  return num >= 20 && num <= 300;
};

// normalización
export const normalizarEnteroCircunferenciaCinturaOnBlur = (value) => {
  if (value === "") return value;

  const num = parseInt(value, 10);
  if (isNaN(num)) return "";

  return String(num);
};

// =============================
// GLUCEMIA (mg/dl)
// =============================

// formato: hasta 3 dígitos
export const validarFormatoGlucemia = (value) => {
  const regex = /^\d{0,3}$/;
  return regex.test(value);
};

// rango NOM: 20 – 999 (0 permitido)
export const validarRangoGlucemia = (value) => {
  const num = parseInt(value, 10);
  if (isNaN(num)) return false;

  if (num === 0) return true;

  return num >= 20 && num <= 999;
};

// normalización
export const normalizarEnteroGlucemiaOnBlur = (value) => {
  if (value === "") return value;

  const num = parseInt(value, 10);
  if (isNaN(num)) return "";

  return String(num);
};

// =============================
// FECHA DE CONSULTA NOM-024
// =============================

// No puede ser a futuro
export const validarFechaConsultaNoFuturo = (fechaStr) => {
  if (!fechaStr) return true;
  const fecha = new Date(fechaStr);
  const ahora = new Date();
  return fecha <= ahora;
};

// No puede ser más de 48 horas atrás
export const validarFechaConsultaNo48hAtras = (fechaStr) => {
  if (!fechaStr) return true;
  const fecha = new Date(fechaStr);
  const hace48h = new Date();
  hace48h.setHours(hace48h.getHours() - 48);
  return fecha >= hace48h;
};

// Si el paciente nació hace menos de 48h,
// la fecha de consulta no puede ser anterior a la fecha de nacimiento
export const validarFechaConsultaNoAntesNacimientoMenor48h = (fechaConsultaStr, fechaNacimientoStr) => {
  if (!fechaConsultaStr || !fechaNacimientoStr) return true;

  const fechaConsulta = new Date(fechaConsultaStr);
  const fechaNacimiento = new Date(`${fechaNacimientoStr}T00:00:00`);
  const ahora = new Date();

  if (isNaN(fechaConsulta.getTime()) || isNaN(fechaNacimiento.getTime())) {
    return true;
  }

  const diferenciaMs = ahora.getTime() - fechaNacimiento.getTime();
  const nacimientoMenor48h = diferenciaMs >= 0 && diferenciaMs <= 48 * 60 * 60 * 1000;

  if (!nacimientoMenor48h) return true;

  return fechaConsulta >= fechaNacimiento;
};

// =============================
// EDAD EN FECHA DE CONSULTA (NOM-024)
// =============================

// Calcula la edad en años cumplidos del paciente al momento
// de la fecha de consulta (no la edad de hoy).
//
// Se usa en TODAS las validaciones y filtros NOM que dependen
// de la edad del paciente al momento del evento clínico.
//
// fechaNacimiento: string ISO ("YYYY-MM-DD") o Date
// fechaConsulta:   string ISO o Date
// retorna: entero (años cumplidos) o null si faltan datos / fechas inválidas

const descomponerFechaCalendario = (fechaStr) => {
  if (!fechaStr) return null;

  const soloFecha = String(fechaStr).split("T")[0];
  const [year, month, day] = soloFecha.split("-").map(Number);

  if (!year || !month || !day) return null;

  return { year, month, day };
};

export const calcularEdadEnFechaConsulta = (fechaNacimiento, fechaConsulta) => {
  if (!fechaNacimiento || !fechaConsulta) return null;

  const fn = descomponerFechaCalendario(fechaNacimiento);
  const fc = descomponerFechaCalendario(fechaConsulta);

  if (!fn || !fc) return null;

  let edad = fc.year - fn.year;

  const aunNoCumple =
    fc.month < fn.month ||
    (fc.month === fn.month && fc.day < fn.day);

  if (aunNoCumple) edad -= 1;

  return edad;
};

// Calcula la edad en MESES cumplidos del paciente al momento
// de la fecha de consulta.
//
// Se usa en filtros NOM que dependen de meses (ej. Battelle: 16-60 meses).
//
// fechaNacimiento: string ISO ("YYYY-MM-DD") o Date
// fechaConsulta:   string ISO o Date
// retorna: entero (meses cumplidos) o null si faltan datos / fechas inválidas
export const calcularEdadMesesEnFechaConsulta = (fechaNacimiento, fechaConsulta) => {
  if (!fechaNacimiento || !fechaConsulta) {
    return null;
  }

  const fn = descomponerFechaCalendario(fechaNacimiento);
  const fc = descomponerFechaCalendario(fechaConsulta);

  if (!fn || !fc) {
    return null;
  }

  let meses =
    (fc.year - fn.year) * 12 +
    (fc.month - fn.month);

  // Si aún no llega el día del mes, no cumple ese mes todavía
  if (fc.day < fn.day) {
    meses -= 1;
  }

  return meses;
};

export const validarFechaConsultaNoAntesNacimiento = (fechaConsultaStr, fechaNacimientoStr) => {
  if (!fechaConsultaStr || !fechaNacimientoStr) return true;

  const fechaConsulta = new Date(fechaConsultaStr);
  const fechaNacimiento = new Date(`${fechaNacimientoStr}T00:00:00`);

  if (isNaN(fechaConsulta.getTime()) || isNaN(fechaNacimiento.getTime())) {
    return true;
  }

  return fechaConsulta >= fechaNacimiento;
};